/**
 * @file ReportViewerModal.js
 * @description Modal overlay that fetches a report as CSV, parses it, and renders
 * it in a read-only spreadsheet view using react-spreadsheet.
 */

import { useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import Spreadsheet from 'react-spreadsheet';
import style from '../ui/modals/modals.module.scss';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import PaginationComponent from '../helpers/PaginationComponent';
import { fetchGeneratedReportCsv } from '../../services/reportingService';
import { getText } from '../../i18n';

const MAX_PREVIEW_BYTES = 5 * 1024 * 1024;
const VIEWER_PAGE_SIZE = 100;

function parseCsvRows(csvText) {
    const result = Papa.parse(csvText, { skipEmptyLines: true });
    return result.data || [];
}

function ReportViewerModal({
    generatedReportId,
    reportName,
    fileSizeBytes,
    onClose,
}) {
    const [headerRow, setHeaderRow] = useState(null);
    const [dataRows, setDataRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const isTooLarge = fileSizeBytes > MAX_PREVIEW_BYTES;

    const totalPages = useMemo(() => {
        const count = Math.ceil(dataRows.length / VIEWER_PAGE_SIZE);
        return count === 0 ? 1 : count;
    }, [dataRows.length]);

    const pageRows = useMemo(() => {
        const start = (currentPage - 1) * VIEWER_PAGE_SIZE;
        return dataRows.slice(start, start + VIEWER_PAGE_SIZE);
    }, [dataRows, currentPage]);

    const columnLabels = useMemo(() => headerRow || undefined, [headerRow]);

    const rowLabels = useMemo(() => {
        const start = (currentPage - 1) * VIEWER_PAGE_SIZE;
        return pageRows.map((_, i) => String(start + i + 1));
    }, [pageRows, currentPage]);

    const spreadsheetData = useMemo(() => {
        if (!headerRow) {
            return null;
        }

        return pageRows.map(row =>
            row.map(cell => ({ value: cell, readOnly: true })),
        );
    }, [headerRow, pageRows]);

    useEffect(() => {
        if (isTooLarge) {
            setIsLoading(false);
            return;
        }

        let isCancelled = false;

        async function loadCsv() {
            setIsLoading(true);
            setError(null);

            try {
                const csvText =
                    await fetchGeneratedReportCsv(generatedReportId);

                if (isCancelled) {
                    return;
                }

                const rows = parseCsvRows(csvText);
                const [header, ...data] = rows;

                setHeaderRow(header || []);
                setDataRows(data);
            } catch (err) {
                if (!isCancelled) {
                    setError('Failed to load report preview.');
                    Toast.error(getText('msg_5d6cab1ddc0a'));
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        }

        loadCsv();

        return () => {
            isCancelled = true;
        };
    }, [generatedReportId, isTooLarge]);

    useEffect(() => {
        function handleEscapeKey(event) {
            if (event.key === 'Escape') {
                onClose();
            }
        }

        document.addEventListener('keydown', handleEscapeKey);
        return () => document.removeEventListener('keydown', handleEscapeKey);
    }, [onClose]);

    return (
        <div className={`${style.container} modal-backdrop`}>
            <div
                className="modal fade show display-block"
                tabIndex="-1"
                aria-modal="true"
                role="dialog"
            >
                <div
                    className={`modal-dialog modal-xl modal-dialog-centered ${style.modalSizing}`}
                    style={{
                        width: 'fit-content',
                        maxWidth: '95vw',
                    }}
                >
                    <div
                        className={`modal-content ${style.modalContent}`}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            maxHeight: '90vh',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '24px',
                            }}
                        >
                            <h5
                                className={`modal-title ${style.title}`}
                                style={{ margin: 0, flex: 1 }}
                            >
                                {reportName || 'Report Preview'}
                                {!isLoading && dataRows.length > 0 && (
                                    <span
                                        style={{
                                            fontSize: '13px',
                                            fontWeight: 400,
                                            color: '#888',
                                            marginLeft: '12px',
                                        }}
                                    >
                                        {dataRows.length} {getText('msg_6c30d2615392')}
                                    </span>
                                )}
                            </h5>
                            <button
                                type="button"
                                className={`btn-close ${style.dismissBtn}`}
                                style={{
                                    position: 'static',
                                    marginLeft: '24px',
                                }}
                                onClick={onClose}
                                aria-label={getText('msg_bbfa773e5a63')}
                            />
                        </div>

                        <div
                            className="report-viewer-spreadsheet-container"
                            style={{
                                flex: '1 1 auto',
                                overflow: 'auto',
                                minHeight: 0,
                                margin: '0 auto',
                                maxWidth: '100%',
                            }}
                        >
                            {isLoading && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        paddingTop: '40px',
                                    }}
                                >
                                    <Spinner />
                                </div>
                            )}

                            {!isLoading && isTooLarge && (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        paddingTop: '40px',
                                    }}
                                >
                                    <i
                                        className="bi bi-exclamation-triangle"
                                        style={{
                                            fontSize: '2rem',
                                            color: '#f0ad4e',
                                            display: 'block',
                                            marginBottom: '12px',
                                        }}
                                    />
                                    <p className="page-description-text">
                                        {getText('msg_e534c4bdf9db')}
                                    </p>
                                </div>
                            )}

                            {!isLoading && error && (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        paddingTop: '40px',
                                    }}
                                >
                                    <p className="page-description-text">
                                        {error}
                                    </p>
                                </div>
                            )}

                            {!isLoading &&
                                !isTooLarge &&
                                !error &&
                                spreadsheetData && (
                                    <Spreadsheet
                                        data={spreadsheetData}
                                        columnLabels={columnLabels}
                                        rowLabels={rowLabels}
                                    />
                                )}
                        </div>

                        {!isLoading &&
                            !isTooLarge &&
                            !error &&
                            totalPages > 1 && (
                                <div
                                    style={{
                                        paddingTop: '12px',
                                        textAlign: 'center',
                                    }}
                                >
                                    <PaginationComponent
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={setCurrentPage}
                                    />
                                </div>
                            )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReportViewerModal;
